import { useEffect, useMemo, useRef, useState } from 'react';

import { useIsDynamicTileServer } from 'src/utils/mapHelpers/hooks/useIsDynamicTileServer';
import { TrackData } from 'src/utils/mapHelpers/trackHelpers/types';
import { getSources } from '../mapApis';

// TODO - pass in selection keys as parameters so it can be used on other maps
const SELECTION_KEYS = {
  SELECTED: 'selected',
};

// External means selection outside of the map component - either through props or through tables

export const useTrackSelection = ({
  mapApis,
  externallySelectedTracks,
  rerunHook = false,
  externallyDeselectedTrackIds = [],
  externallySelectTrack,
}: {
  mapApis;
  externallySelectedTracks: TrackData[];
  rerunHook?: boolean;
  externallyDeselectedTrackIds?: number[];
  externallySelectTrack?: (trackIds: number[]) => void;
}) => {
  const [activelySelectedTracks, setActivelySelectedTracks] = useState<TrackData[]>([]);
  // selectedOperationsRef holds the current value synchronously for use in the same render cycle.
  // selectedOperations state is kept in sync to trigger re-renders when it changes.
  const selectedOperationsRef = useRef<TrackData[]>([]);
  const [selectedOperations, setSelectedTracks] = useState<TrackData[]>([]);
  const isDynamicTileServer = useIsDynamicTileServer();

  const normaliseId = (operation: TrackData) =>
    isDynamicTileServer ? operation.properties.id : operation.id;

  const setSelectedOperations = (operationsTrackData: TrackData[], addOnly = false) => {
    if (!operationsTrackData.length) {
      selectedOperationsRef.current = [];
      setSelectedTracks([]);
      externallySelectTrack?.([]);
      return;
    }
    const triggeredOperations = operationsTrackData.map(operation => ({
      ...operation,
      // reformat so the operation id is in the same place for all maps with or without tile server
      id: normaliseId(operation),
    }));
    // Use ref for toggle logic so we read the current value, not the stale state
    const current = selectedOperationsRef.current;
    // Deselect when clicking on an already active track (not applicable for marquee select)
    const deselected = addOnly
      ? []
      : current.flatMap(({ id }) =>
          triggeredOperations.map(({ id }) => id).includes(id) ? id : []
        );
    const newlySelectedOperations = triggeredOperations.filter(
      ({ id }) => !deselected.includes(id)
    );
    const remainingSelectedOperations = current.filter(
      ({ id }) => !deselected.includes(id)
    );
    const currentlySelectedOperations = addOnly
      ? [...newlySelectedOperations, ...remainingSelectedOperations]
      : newlySelectedOperations;

    // Update ref synchronously before state (ref is readable on same render cycle)
    selectedOperationsRef.current = currentlySelectedOperations;
    setSelectedTracks(currentlySelectedOperations);
    externallySelectTrack?.(currentlySelectedOperations.map(({ id }) => id));
  };

  // allSelectedTracks reads from the ref so it always has the current local selection,
  // even on the same render cycle where setSelectedTracks was just called.
  // selectedOperations is kept as a dep so this memo re-runs when local state settles.
  const allSelectedTracks = useMemo(() => {
    const local = selectedOperationsRef.current;
    const localIds = local.map(({ id }) => id);
    const filteredExternal = localIds.length > 0
      ? externallySelectedTracks.filter(t => localIds.includes(normaliseId(t)))
      : externallySelectedTracks;
    // Deduplicate by ID (queryRenderedFeatures can return the same track multiple times
    // when it spans tile boundaries)
    const combined = [...filteredExternal, ...local];
    const seen = new Set<number>();
    return combined.filter(t => {
      const id = t.id;
      if (seen.has(id)) return false;
      seen.add(id);
      return true;
    });
  }, [externallySelectedTracks, selectedOperations]);

  const currentlySelectedOperationIds = allSelectedTracks.map(({ id }) => id);

  const deselectedTracks = useMemo(
    () =>
      activelySelectedTracks.flatMap(track =>
        !currentlySelectedOperationIds.includes(track.id) ||
          externallyDeselectedTrackIds.includes(track.id)
          ? track
          : []
      ),
    [activelySelectedTracks, allSelectedTracks, externallyDeselectedTrackIds]
  );

  useEffect(() => {
    if (externallySelectedTracks.length === 0) {
      selectedOperationsRef.current = [];
      setSelectedTracks([]);
    }
  }, [externallySelectedTracks]);

  useUpdateTrackFeatureState(
    allSelectedTracks,
    deselectedTracks,
    mapApis,
    setActivelySelectedTracks,
    rerunHook
  );
  return { setSelectedOperations, selectedOperations };
};

const useUpdateTrackFeatureState = (
  selectedTracks: TrackData[],
  deselectedTracks: TrackData[],
  mapApis,
  setActivelySelectedTracks,
  rerunHook: boolean
) => {
  useEffect(() => {
    if (mapApis) {
      const mapSources = getSources(mapApis);

      selectedTracks.forEach(operation => {
        const selectionValue = {};
        selectionValue[SELECTION_KEYS.SELECTED] = true;

        const object = {
          id: operation.id,
          source: operation.layer.source,
          sourceLayer: operation.layer['source-layer'],
        };

        if (Object.keys(mapSources).includes(object.source)) {
          try {
            mapApis.setFeatureState(object, selectionValue);
          } catch {
            console.error('setFeatureState', object);
          }
        }
      });

      deselectedTracks.forEach(operation => {
        const object = {
          id: operation.id,
          source: operation.layer.source,
          sourceLayer: operation.layer['source-layer'],
        };
        if (Object.keys(mapSources).includes(object.source)) {
          try {
            mapApis.removeFeatureState(object, SELECTION_KEYS.SELECTED);
          } catch {
            console.error('removeFeatureState', object);
          }
        }
      });
      setActivelySelectedTracks(selectedTracks);
    }
  }, [selectedTracks, deselectedTracks, rerunHook]);
};
